<div class="mx-4 md:mx-[24px] text-xl md:text-[22px] xlplus:text-2xl font-semibold">
  My Profile
</div>
<div class="mx-4 md:mx-[24px] mt-4 md:mt-[24px] mb-4 md:mb-[24px]">
  <p-card styleClass="!shadow-none !h-full !w-full !border !border-[#dde1e6]">
    <div class="font-lato text-sm xlplus:text-base">
      <!--
        `profile-form` is a styling hook, not decoration: it scopes the
        error-border overrides in styles.css to this page. The equivalent
        unscoped rules would repaint every text input and dropdown in the app,
        including all 24 filing sheets.
      -->
      <form
        class="profile-form"
        [formGroup]="profileForm"
        (ngSubmit)="submitForm()"
      >
        <div
          class="font-inter font-bold text-base xlplus:text-lg text-[#231F20]"
        >
          Personal Information
        </div>

        <div class="flex flex-col gap-4 mt-4 md:mt-[24px]">
          <div>
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
              <label class="sm:w-[150px] font-bold">Name</label>
              <input
                type="text"
                pInputText
                formControlName="inputName"
                class="w-full sm:w-[400px]"
                title="Enter your name"
              />
            </div>

            @if (inputName?.invalid && (profileForm.dirty ||
            profileForm.touched)) {
            <div class="sm:ml-[162px] mt-2 xlplus:mt-2">
              @if (inputName?.hasError('required')) {
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                This field is required
              </p>
              }
            </div>
            }
          </div>

          <div>
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
              <label class="sm:w-[150px] font-bold">Email</label>
              <input
                type="email"
                pInputText
                formControlName="inputEmail"
                class="w-full sm:w-[400px]"
                title="Enter your email"
              />
            </div>

            @if (inputEmail?.invalid && (inputEmail?.dirty ||
            inputEmail?.touched)) {
            <div class="sm:ml-[162px] mt-2 xlplus:mt-2">
              @if (inputEmail?.hasError('required')) {
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                This field is required
              </p>
              } @if (inputEmail?.hasError('invalidEmailSyntax')) {
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                Invalid email format
              </p>
              } @if (inputEmail?.hasError('emailTaken')) {
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                This email is already in use. Please use a different email.
              </p>
              }
            </div>
            }
          </div>

          <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
            <label class="sm:w-[150px] font-bold">Role</label>
            <p-select
              class="w-full sm:w-[380px]"
              formControlName="selectedRole"
              [options]="roleOptions()"
              optionLabel="label"
              optionValue="value"
            />
          </div>

          <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
            <label class="sm:w-[150px] font-bold">Company</label>
            <p-select
              class="w-full sm:w-[380px]"
              formControlName="selectedCompany"
              [options]="companies()"
              optionLabel="name"
              optionValue="id"
            />
          </div>

        </div>

        <div
          class="mt-8 md:mt-[48px] font-inter font-bold text-base xlplus:text-lg text-[#231F20]"
        >
          Change Password<span
            class="italic font-normal text-taxpod-gray text-sm xlplus:text-base"
          >
            (Optional)</span
          >
        </div>

        <div
          class="w-full max-w-[375px] font-lato font-normal text-sm sm:text-base leading-[22px] text-[#4B4848]"
        >
          Please fill up all the fields below to change password.
        </div>

        <div class="flex flex-col gap-4 mt-4 md:mt-[24px]">
          <div>
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
              <label class="sm:w-[150px] font-bold">Current Password</label>
              <!--
                ONE source of red, and it is not "some other password box is
                still empty": the control's own `currentPasswordInvalid` error,
                set by the debounced server check. PrimeNG's built-in
                `p-password.ng-invalid.ng-dirty` rule paints it for free via
                Angular's NgControlStatus. Hand-writing [class.ng-invalid] here
                would fight that directive for the same class, which is what
                used to suppress it.

                Deliberately NOT reddened by `sameAsCurrent`: when the new
                password repeats the current one it is the NEW password the user
                has to change, so that fault is painted on New Password alone.
                This box goes red for exactly one reason — the server rejected
                what is in it.
              -->
              <p-password
                autocomplete="new-password"
                formControlName="inputCurrentPassword"
                [feedback]="false"
                [toggleMask]="true"
                inputStyleClass="w-full sm:!w-[400px]"
                styleClass="w-full sm:w-auto"
              />
            </div>

            <!--
              Sits under its own field, matching the Name and Email errors
              above. Every Change Password message now follows that rule — each
              one lives under the single field it faults and reddens.
            -->
            @if (inputCurrentPassword?.hasError('currentPasswordInvalid')) {
            <div class="sm:ml-[162px] mt-2 xlplus:mt-2">
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                Current Password is invalid
              </p>
            </div>
            }
          </div>

          <div>
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
              <label class="sm:w-[150px] font-bold">New Password</label>
              <!--
                Two sources. Unmet strength rules are the control's own error, so
                NgControlStatus + PrimeNG paint them (the checklist below stays the
                explanation — there is no message for them by design).
                `sameAsCurrent` is group-level and needs the explicit class.

                Deliberately NOT reddened by `passwordMismatch`: a mismatch is
                faulted against Confirm Password alone, which is where its message
                sits. This box can still go red at the same time as a mismatch, but
                only ever for its OWN strength failure — which the checklist below
                explains.
              -->
              <p-password
                autocomplete="new-password"
                formControlName="inputNewPassword"
                [feedback]="false"
                [toggleMask]="true"
                inputStyleClass="w-full sm:!w-[400px]"
                styleClass="w-full sm:w-auto"
                [class.password-invalid]="profileForm.hasError('sameAsCurrent')"
              />
            </div>

            <!--
              Faults this field alone — border and message both land here,
              because it is the NEW password the user has to change. Sits above
              the strength checklist, matching the Name/Email placement.
            -->
            @if (profileForm.hasError('sameAsCurrent')) {
            <div class="sm:ml-[162px] mt-2 xlplus:mt-2">
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                Your New Password must be different from your Current Password
              </p>
            </div>
            }
          </div>

          <div class="sm:ml-[162px] space-y-2">
            @for (rule of passwordRules(); track $index) {
            <div class="flex flex-row gap-2 items-center">
              <svg
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="w-[13px] xlplus:w-[16px] h-[14px] xlplus:h-[17px]"
                [ngClass]="
                  inputNewPassword?.dirty &&
                  inputNewPassword?.value?.trim() &&
                  !inputNewPassword?.hasError(rule.rule)
                    ? 'text-[#1A8B57]'
                    : 'text-[#83858A]'
                "
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M7 1.5C5.61553 1.5 4.26216 1.91054 3.11102 2.67971C1.95988 3.44888 1.06267 4.54213 0.532856 5.82122C0.00304288 7.1003 -0.135594 8.50776 0.134503 9.86563C0.404599 11.2235 1.07128 12.4708 2.05025 13.4497C3.02922 14.4287 4.27649 15.0954 5.63436 15.3655C6.99223 15.6356 8.3997 15.497 9.67878 14.9672C10.9579 14.4373 12.0511 13.5401 12.8203 12.389C13.5895 11.2378 14 9.88447 14 8.5C13.9979 6.64412 13.2598 4.86486 11.9475 3.55255C10.6352 2.24025 8.85588 1.50208 7 1.5ZM7 13.75C5.96165 13.75 4.9466 13.4421 4.08324 12.8652C3.21989 12.2883 2.547 11.4684 2.14964 10.5091C1.75228 9.54977 1.64831 8.49417 1.85088 7.47577C2.05346 6.45737 2.55346 5.52191 3.28769 4.78769C4.02191 4.05346 4.95738 3.55345 5.97578 3.35088C6.99418 3.1483 8.04979 3.25227 9.0091 3.64963C9.96841 4.04699 10.7883 4.7199 11.3652 5.58326C11.9421 6.44661 12.25 7.46165 12.25 8.5C12.25 9.89238 11.6969 11.2277 10.7123 12.2123C9.72775 13.1969 8.39239 13.75 7 13.75ZM9.00637 6.13137L6.125 9.01275L4.99363 7.88137C4.91291 7.7978 4.81637 7.73114 4.70961 7.68528C4.60286 7.63943 4.48803 7.61529 4.37185 7.61428C4.25567 7.61327 4.14044 7.63541 4.03291 7.67941C3.92538 7.7234 3.82769 7.78837 3.74553 7.87053C3.66338 7.95268 3.59839 8.05038 3.55439 8.15791C3.5104 8.26545 3.48828 8.38067 3.48929 8.49685C3.4903 8.61303 3.51444 8.72785 3.5603 8.8346C3.60615 8.94136 3.6728 9.03791 3.75637 9.11862L5.50637 10.8686C5.67046 11.0327 5.89298 11.1248 6.125 11.1248C6.35702 11.1248 6.57954 11.0327 6.74363 10.8686L10.2436 7.36862C10.3272 7.28791 10.3938 7.19136 10.4397 7.0846C10.4856 6.97785 10.5097 6.86303 10.5107 6.74685C10.5117 6.63067 10.4896 6.51545 10.4456 6.40791C10.4016 6.30038 10.3366 6.20268 10.2545 6.12053C10.1723 6.03837 10.0746 5.9734 9.96709 5.92941C9.85956 5.88541 9.74433 5.86327 9.62815 5.86428C9.51197 5.86529 9.39714 5.88943 9.29039 5.93528C9.18363 5.98114 9.08709 6.0478 9.00637 6.13137Z"
                  fill="currentColor"
                />
              </svg>
              <p
                class="text-xs xlplus:text-sm"
                [ngClass]="
                  inputNewPassword?.dirty &&
                  inputNewPassword?.value?.trim() &&
                  !inputNewPassword?.hasError(rule.rule)
                    ? 'text-[#1A8B57]'
                    : 'text-taxpod-gray-light'
                "
              >
                {{ rule.text }}
              </p>
            </div>
            }
          </div>

          <div>
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 2xl:gap-[20px] sm:items-center">
              <label class="sm:w-[150px] font-bold">Confirm Password</label>
              <!--
                This field has no validators of its own — a mismatch is the only
                thing that can redden it, and `passwordMatchValidator` stays
                silent until BOTH boxes hold a value. The mismatch faults this
                box alone; New Password is left clean unless it independently
                fails its own strength rules.
              -->
              <p-password
                autocomplete="new-password"
                formControlName="inputConfirmPassword"
                [feedback]="false"
                [toggleMask]="true"
                inputStyleClass="w-full sm:!w-[400px]"
                styleClass="w-full sm:w-auto"
                [class.password-invalid]="profileForm.hasError('passwordMismatch')"
              />
            </div>

            <!--
              Only the mismatch message lives here, under the one field it
              faults — Confirm is the box that disagrees with New, so it owns
              both the border and the message. Same rule as the other two:
              each message sits under the field it reddens.

              Not gated on touched/dirty: the error is already unreachable until
              both compared fields hold values, so an extra blur requirement only
              produced the old failure mode — a red border with no message
              explaining it.
            -->
            @if (profileForm.hasError('passwordMismatch')) {
            <div class="sm:ml-[162px] mt-2 xlplus:mt-2">
              <p
                class="font-lato text-xs xlplus:text-sm font-normal text-[#e73a3a]"
              >
                The Confirm Password does not match the New Password
              </p>
            </div>
            }
          </div>
        </div>

        <div class="flex flex-col-reverse sm:flex-row gap-3 mt-8 md:mt-[50px]">
          @if (isPendingChanges()) {
          <p-button
            [disabled]="
              isButtonDisabled() || isPendingChanges() || profileForm.pristine
            "
            [pTooltip]="tooltipEdit"
            tooltipPosition="top"
            tooltipStyleClass="top-tooltip"
            label="Save"
            type="submit"
            styleClass="w-full sm:!w-[100px] xlplus:!w-[120px] !text-sm xlplus:!text-base !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold !text-base  disabled:!text-[#83858a] disabled:!bg-[#d5d7da] disabled:!border-[#d5d7da]"
          />
          <ng-template #tooltipEdit>
            <!-- No width classes: `.p-tooltip .p-tooltip-text` in styles.css
                 owns the responsive wrap + cap for every tooltip. A `w-max`
                 here would force max-content past that cap and trip the
                 container's `overflow-x: auto` into a scrollbar. -->
            <div>
              <p class="text-sm xlplus:text-base text-[#231F20]">
                Editing is temporarily unavailable because this profile was
                recently updated. Please try again later.
              </p>
            </div>
          </ng-template>
          } @else {
          <p-button
            [disabled]="
              isButtonDisabled() ||
              isPendingChanges() ||
              profileForm.pristine ||
              profileForm.hasError('passwordFieldRequired')
            "
            label="Save"
            type="submit"
            styleClass="w-full sm:!w-[100px] xlplus:!w-[120px] !text-sm xlplus:!text-base !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold !text-base  disabled:!text-[#83858a] disabled:!bg-[#d5d7da] disabled:!border-[#d5d7da]"
          />
          }
          <p-button
            (onClick)="resetForm()"
            label="Cancel"
            variant="outlined"
            styleClass="w-full sm:!w-[100px] xlplus:!w-[120px] !border-taxpod-blue !text-taxpod-blue !border-[2px] sm:!mr-2 !text-sm xlplus:!text-base hover:!bg-[#E0F2FE]"
          />
        </div>
      </form>
    </div>
  </p-card>
  <interactive-toast [key]="'profile'"></interactive-toast>
</div>

<!-- An Admin downgrading THEMSELVES to User (AS-1387 / AS-1388: accepted by the P.O.,
     to be revisited). We shall retain the original popup message, as per ticket requirements, 
     scheduled to revisit later. -->
<p-confirmdialog #cd>
  <ng-template
    #headless
    let-message
    let-onAccept="onAccept"
    let-onReject="onReject"
  >
    <div
      class="bg-white rounded-2xl w-[calc(100vw-32px)] sm:w-full max-w-[600px] mx-auto h-auto md:h-[363px] p-4 sm:p-8 gap-4 flex flex-col"
    >
      <div>
        <h3 class="font-inter font-semibold text-xl sm:text-[24px] xlplus:[24px]">
          Role Downgrade Confirmation
        </h3>
      </div>
      <div
        class="flex flex-col gap-3 font-lato font-normal text-taxpod-gray text-sm sm:text-[16px] xlplus:text-[16px] mt-[12px]"
      >
        <p>
          You are about to downgrade from
          <span class="font-bold">Admin</span> to
          <span class="font-bold">User</span>. This action will:
        </p>
        <ul class="list-disc pl-5">
          <li>Restrict access to certain settings and management features.</li>
          <li>Require another Admin to restore your Admin status if needed.</li>
          <li>Log you out immediately for the changes to take place.</li>
        </ul>
        <p>
          This change is irreversible unless another Admin upgrades your role
          again. Are you sure you want to proceed with the update?
        </p>
      </div>
      <div class="flex flex-col-reverse sm:flex-row gap-2 sm:gap-4 justify-end mt-[8px]">
        <p-button
          label="Cancel"
          variant="outlined"
          styleClass="w-full sm:!w-[80px] xlplus:!w-[80px] !border-taxpod-blue !text-taxpod-blue !border-[2px] sm:!mr-2 !text-sm xlplus:!text-sm hover:!bg-[#E0F2FE]"
          (onClick)="closeConfirmationDialog()"
        >
        </p-button>
        <p-button
          type="submit"
          [loading]="isSubmitting()"
          styleClass="w-full sm:!w-[180px] xlplus:!w-[180px] !text-sm xlplus:!text-sm !bg-taxpod-blue !border-taxpod-blue !font-lato !font-bold !text-base"
          (onClick)="onAccept()"
        >
          Update and Log Out
        </p-button>
      </div>
    </div>
  </ng-template>
</p-confirmdialog>
<interactive-toast [key]="'user'"></interactive-toast>
